/**
 * Session management utilities for custom domain
 * Handles cleanup of stale tokens and cross-domain session conflicts
 */

import { supabase } from "@/integrations/supabase/client";
import { CANONICAL_HOST, isDev } from "./domain";

const DOMAIN_MIGRATION_KEY = "ayni_domain_migrated_v2";
const OLD_DOMAIN_MARKERS = ["vercel.app"]; // For Vercel preview cleanup

/**
 * Check if there are conflicting session tokens from preview/dev domains
 * that need to be cleared
 */
export function hasOldDomainTokens(): boolean {
  // Check for markers that indicate session was created on preview domain
  const storedOrigin = localStorage.getItem("supabase.auth.origin");
  if (storedOrigin && OLD_DOMAIN_MARKERS.some(marker => storedOrigin.includes(marker))) {
    return true;
  }
  
  // Check if referrer is from preview domain
  if (OLD_DOMAIN_MARKERS.some(marker => document.referrer.includes(marker))) {
    return true;
  }
  
  return false;
}

/**
 * Clear all auth-related storage to force fresh login
 */
export function clearAuthStorage(): void {
  const keysToRemove: string[] = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.startsWith("supabase.auth") ||
      key.startsWith("sb-") ||
      key.includes("-auth-token") ||
      key.includes("supabase")
    )) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach((key) => localStorage.removeItem(key));
  
  // Also clear sessionStorage
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key && (
      key.startsWith("supabase.auth") ||
      key.startsWith("sb-") ||
      key.includes("-auth-token")
    )) {
      sessionStorage.removeItem(key);
    }
  }
}

/**
 * Mark that domain migration has been completed
 */
export function markMigrationComplete(): void {
  localStorage.setItem(DOMAIN_MIGRATION_KEY, "true");
}

/**
 * Check if domain migration cleanup has been done
 */
export function hasMigrationCompleted(): boolean {
  return localStorage.getItem(DOMAIN_MIGRATION_KEY) === "true";
}

/**
 * One-time cleanup for users coming from old domain
 * Forces re-login to establish proper session on new domain
 */
export async function performDomainMigration(): Promise<boolean> {
  // Skip in dev
  if (isDev()) return false;
  
  // Skip if already migrated
  if (hasMigrationCompleted()) return false;
  
  // Skip if we're not on canonical domain yet
  if (window.location.hostname !== CANONICAL_HOST) return false;
  
  // Check if we have potentially stale tokens
  const hasStaleTokens = hasOldDomainTokens();
  
  if (hasStaleTokens) {
    console.log("[Session] Detected old domain tokens, clearing auth storage...");
    
    // Sign out from Supabase to clear server-side session
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch (e) {
      console.warn("[Session] Sign out failed, continuing with local cleanup:", e);
    }
    
    // Clear local storage
    clearAuthStorage();
    
    // Mark migration as complete
    markMigrationComplete();
    
    return true;
  }
  
  // Mark as complete even if no cleanup needed
  markMigrationComplete();
  return false;
}

/**
 * Validate current session and re-fetch if needed
 * Returns true if session is valid
 */
export async function validateSession(): Promise<boolean> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error("[Session] Error fetching session:", error);
      return false;
    }
    
    if (!session) {
      return false;
    }
    
    // Session exists, validate it's not expired or about to expire
    const expiresAt = session.expires_at;
    if (expiresAt && expiresAt * 1000 < Date.now() + 60000) {
      console.log("[Session] Session expired or expiring soon, attempting refresh...");
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError || !refreshData.session) {
        console.error("[Session] Failed to refresh session:", refreshError);
        return false;
      }
    }
    
    return true;
  } catch (e) {
    console.error("[Session] Validation error:", e);
    return false;
  }
}

/**
 * Initialize session management
 * Should be called early in app lifecycle
 */
export async function initializeSession(): Promise<void> {
  // Perform one-time domain migration if needed
  const didMigrate = await performDomainMigration();
  
  if (didMigrate) {
    console.log("[Session] Domain migration completed, user needs to re-login");
  }
  
  // Validate existing session
  await validateSession();
}
